import CloseIcon from '@mui/icons-material/Close';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Drawer,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { HomeBannerFilters } from '../home-banners.hook';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    filters: HomeBannerFilters;
    onApplyFilters: (filters: HomeBannerFilters) => void;
    onClearFilters: () => void;
};

export default function HomeBannersDrawerFilters({
    isOpen,
    onClose,
    filters,
    onApplyFilters,
    onClearFilters,
}: Props) {
    const [draftFilters, setDraftFilters] = useState(filters);

    useEffect(() => {
        if (isOpen) {
            setDraftFilters(filters);
        }
    }, [filters, isOpen]);

    const updateFilter = <K extends keyof HomeBannerFilters>(key: K, value: HomeBannerFilters[K]) => {
        setDraftFilters((current) => ({ ...current, [key]: value }));
    };

    const filterCardSx = {
        borderRadius: 1,
        boxShadow: 'none',
        borderColor: 'grey.200',
    };

    const statusOptions = [
        { value: 'active', label: 'Ativo' },
        { value: 'inactive', label: 'Inativo' },
    ];
    const typeOptions = [
        { value: 'one-image', label: 'Uma imagem' },
        { value: 'two-images', label: 'Duas imagens' },
        { value: 'three-images', label: 'Três imagens' },
    ];
    const tierOptions = [
        { value: 'all', label: 'Todos' },
        { value: 'silver', label: 'Prata' },
        { value: 'gold', label: 'Ouro' },
        { value: 'diamond', label: 'Diamante' },
    ];

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100vw', sm: 560, md: 620 },
                    maxWidth: '100vw',
                    backgroundColor: 'grey.50',
                },
            }}
        >
            <Stack sx={{ height: '100%' }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            Filtros de Banners
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Refine a listagem com os critérios desejados
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
                    <Stack spacing={2}>
                        <Card variant="outlined" sx={filterCardSx}>
                            <CardContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Status do banner
                                    </Typography>
                                    <Autocomplete
                                        options={statusOptions}
                                        value={statusOptions.find((option) => option.value === draftFilters.status) || null}
                                        onChange={(_, value) => updateFilter('status', value?.value as HomeBannerFilters['status'] || '')}
                                        getOptionLabel={(option) => option.label}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="filled" label="Status" />
                                        )}
                                    />
                                </Stack>
                            </CardContent>
                        </Card>

                        <Card variant="outlined" sx={filterCardSx}>
                            <CardContent>
                                <Stack spacing={2}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Dados do banner
                                    </Typography>
                                    <Autocomplete
                                        options={typeOptions}
                                        value={typeOptions.find((option) => option.value === draftFilters.type) || null}
                                        onChange={(_, value) => updateFilter('type', value?.value as HomeBannerFilters['type'] || '')}
                                        getOptionLabel={(option) => option.label}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="filled" label="Tipo do banner" />
                                        )}
                                    />
                                    <Autocomplete
                                        options={tierOptions}
                                        value={tierOptions.find((option) => option.value === draftFilters.tier) || null}
                                        onChange={(_, value) => updateFilter('tier', value?.value || '')}
                                        getOptionLabel={(option) => option.label}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="filled" label="Tier" />
                                        )}
                                    />
                                    <TextField
                                        variant="filled"
                                        label="Prioridade"
                                        type="number"
                                        value={draftFilters.priority}
                                        onChange={(event) => updateFilter('priority', event.target.value)}
                                        inputProps={{ min: 0 }}
                                    />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                </Box>

                <Divider />

                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ p: 2.5 }}>
                    <Button variant="outlined" onClick={() => { onClearFilters(); }}>
                        Limpar
                    </Button>
                    <Button variant="contained" onClick={() => onApplyFilters(draftFilters)}>
                        Aplicar filtros
                    </Button>
                </Stack>
            </Stack>
        </Drawer>
    );
}